import { useFragment, useQuery } from '@apollo/client';
import { CREATE_COMPONENT_REQUEST_VIEW } from '@api/fragments/comments';
import { GET_USERS_FOR_SELECT } from '@api/queries/users';
import { GET_COMPONENT_VENDORS_BY_IDS } from '@api/queries/vendor';
import { type CreateComponentRequestViewFragment } from '@gql/graphql';
import { type Maybe } from '@models/Maybe';
import { type SimpleUser } from '@models/SimpleUser';
import { useChunkedQueryConnection } from '@models/useChunkedQueryConnection';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type RequestRow } from '@pages/notification-components/components/RequestsCard/RequestsTable/columns';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';

/**
 * Resolves the single selected request for the info card. The row comes from
 * the paged table; the live cache entry wins so status updates written to the
 * cache show up without waiting for the table to reload.
 */
export function useComponentCreateRequestDetails(
    row: Maybe<RequestRow>,
): Maybe<ComponentCreateRequestMapped> {
    const { data: cached, complete } = useFragment<
        CreateComponentRequestViewFragment,
        Record<string, never>
    >({
        fragment: CREATE_COMPONENT_REQUEST_VIEW,
        fragmentName: 'CreateComponentRequestView',
        from: { __typename: 'ComponentCreateRequestView', id: row?.id ?? '' },
    });
    // An incomplete fragment (not in the cache yet, or partially written)
    // would give partial data, so the table row is the fallback.
    const node: Maybe<RequestRow> = row && complete ? cached : row;

    // useChunkedQueryConnection de-duplicates the ids itself.
    const userIds = (
        node
            ? [
                  node.requesterID,
                  ...node.comments.map((comment) => comment.userID),
              ]
            : []
    ).filter(Boolean);
    // Chunking keeps every referenced user resolvable instead of showing
    // "Unknown User".
    const { nodes: userNodes } = useChunkedQueryConnection(
        GET_USERS_FOR_SELECT,
        userIds,
        (chunk) => ({ first: chunk.length, where: { idIn: chunk } }),
        { rootField: 'users', skip: userIds.length === 0 },
    );

    const componentVendorIds = node?.componentVendorID
        ? [node.componentVendorID]
        : [];
    const { data: vendorsData } = useQuery(GET_COMPONENT_VENDORS_BY_IDS, {
        variables: { ids: componentVendorIds },
        skip: componentVendorIds.length === 0,
    });

    if (!node) {
        return null;
    }

    const getUser = (userId: string): SimpleUser => {
        const user = userNodes.find((u) => u.id === userId);
        return user
            ? {
                  id: user.id,
                  firstName: user.firstName,
                  lastName: user.lastName,
              }
            : { id: userId, firstName: 'Unknown', lastName: 'User' };
    };

    return {
        ...node,
        requester: getUser(node.requesterID),
        comments: node.comments.map((comment) => ({
            content: comment.comment,
            createTime: comment.createTime,
            user: getUser(comment.userID),
        })),
        componentVendor: flatMapEdges(vendorsData?.componentVendors).find(
            (vendor) => vendor.id === node.componentVendorID,
        ),
    };
}
